<!-- PriorityRanking.svelte - Reusable Priority Ranking Display Component -->

<script>
	/**
	 * @typedef {Object} PriorityItem
	 * @property {string} name - The name of the priority item
	 * @property {number} score - The priority score (0-100)
	 * @property {string} [pathKey] - Optional path key identifier
	 * @property {number[]} [values] - Optional array of values
	 */

	/** @type {PriorityItem[]} */
	let { 
		priorities = [],
		title = "Focus on these 3 this week:",
		lowerPriorityTitle = "Lower Priority (protect your energy from these):",
		showScores = true,
		scoreLabel = "Focus Score",
		maxTopPriorities = 3,
		showLowerPriorities = true
	} = $props();

	// Derived values
	let topPriorities = $derived(priorities.slice(0, maxTopPriorities));
	let lowerPriorities = $derived(priorities.slice(maxTopPriorities));
	let hasLowerPriorities = $derived(lowerPriorities.length > 0);

	/**
	 * Get medal emoji for position
	 * @param {number} index - 0-indexed position
	 * @returns {string} Medal emoji
	 */
	function getMedal(index) {
		const medals = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣"];
		return medals[index] || `${index + 1}`;
	}
</script>

{#if priorities.length > 0}
	<div class="priority-ranking">
		<h2>{title}</h2>
		
		<div class="top-priorities">
			{#each topPriorities as priority, index}
				<div class="priority-item priority-{index + 1}">
					<div class="priority-medal">
						{getMedal(index)}
					</div>
					<div class="priority-content">
						<h3>{priority.name}</h3>
						{#if showScores}
							<div class="priority-score">
								{scoreLabel}: {Math.round(priority.score)}/100
							</div>
						{/if}
					</div>
				</div>
			{/each}
		</div>

		{#if showLowerPriorities && hasLowerPriorities}
			<div class="lower-priorities">
				<h3>{lowerPriorityTitle}</h3>
				{#each lowerPriorities as priority}
					<div class="priority-item lower-priority">
						<span class="priority-name">{priority.name}</span>
						{#if showScores}
							<span class="priority-score-inline">{Math.round(priority.score)}/100</span>
						{/if}
					</div>
				{/each}
			</div>
		{/if}
	</div>
{/if}

<style>
	/* Priority Ranking Container */
	.priority-ranking {
		max-width: 600px;
		margin: 2rem auto;
		padding: 2rem;
		background: white;
		border-radius: 15px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	}

	.priority-ranking h2 {
		color: #084b83;
		text-align: center;
		margin-bottom: 2rem;
		font-size: 1.5rem;
	}

	/* Top Priorities Section */
	.top-priorities {
		margin-bottom: 2rem;
	}

	.priority-item {
		display: flex;
		align-items: center;
		gap: 1rem;
		padding: 1rem;
		margin-bottom: 1rem;
		border-radius: 10px;
		transition: all 0.3s ease;
	}

	.priority-item:hover {
		transform: translateX(4px);
	}

	/* Medal position backgrounds */
	.priority-1 {
		background: linear-gradient(135deg, #ffd700, #ffed4e);
		border: 2px solid #ffd700;
	}

	.priority-2 {
		background: linear-gradient(135deg, #c0c0c0, #e8e8e8);
		border: 2px solid #c0c0c0;
	}

	.priority-3 {
		background: linear-gradient(135deg, #cd7f32, #daa520);
		border: 2px solid #cd7f32;
	}

	/* Additional positions (4th, 5th) in case maxTopPriorities > 3 */
	.priority-4,
	.priority-5 {
		background: linear-gradient(135deg, #9cfffa, #d6fffd);
		border: 2px solid #9cfffa;
	}

	/* Medal emoji */
	.priority-medal {
		font-size: 2rem;
		flex-shrink: 0;
	}

	/* Priority content */
	.priority-content {
		flex: 1;
	}

	.priority-content h3 {
		margin: 0 0 0.5rem 0;
		color: #333;
		font-size: 1.2rem;
	}

	.priority-score {
		font-size: 0.9rem;
		color: #666;
		font-weight: 600;
	}

	/* Lower Priorities Section */
	.lower-priorities {
		border-top: 2px solid #e0e0e0;
		padding-top: 1.5rem;
	}

	.lower-priorities h3 {
		color: #666;
		font-size: 1.1rem;
		margin-bottom: 1rem;
	}

	.lower-priority {
		background: #f8f9fa;
		border: 1px solid #e0e0e0;
		opacity: 0.7;
		justify-content: space-between;
	}

	.lower-priority:hover {
		opacity: 1;
	}

	.priority-name {
		flex: 1;
		color: #666;
	}

	.priority-score-inline {
		font-size: 0.9rem;
		color: #888;
		font-weight: 600;
	}

	/* Responsive Design */
	@media (max-width: 768px) {
		.priority-ranking {
			margin: 1rem;
			padding: 1rem;
		}

		.priority-item {
			padding: 0.75rem;
		}

		.priority-medal {
			font-size: 1.5rem;
		}

		.priority-content h3 {
			font-size: 1rem;
		}

		.priority-score {
			font-size: 0.85rem;
		}
	}
</style>
